import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowLeft,
  MapPin,
  Building,
  Calendar,
  DollarSign,
  Clock,
  Bookmark,
  Share,
  Mail,
  Linkedin,
  Briefcase,
  MessageCircle,
} from 'lucide-react';
import Navbar from '../Navbar';
import Avatar from '../shared/Avatar';

const OpportunityDetail = ({ 
  currentUser, 
  opportunity, 
  onSendMessage,
  isOwnPost = false,
  backLinkUrl = '/student/dashboard',
  backLinkText = 'Back to Opportunities',
  showSaveShare = false,
  showNavbar = true,
  isMessagingWithAdvisor = false,
  brandLogoUrl,
  primaryColor,
  secondaryColor,
  accentColor,
  neutralColor,
}) => {
  const [isSaved, setIsSaved] = useState(false);

  const colors = useMemo(
    () => ({
      primary: primaryColor || '#5489B6',
      secondary: secondaryColor || '#002949',
      accent: accentColor || '#5B5E5A',
      neutral: neutralColor || '#666666',
    }),
    [primaryColor, secondaryColor, accentColor, neutralColor],
  );

  const handleSave = () => {
    setIsSaved(!isSaved);
    // TODO: Implement save functionality
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: opportunity.title,
        text: opportunity.description,
        url: window.location.href,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleSendMessage = async () => {
    if (!onSendMessage) {
      console.error('onSendMessage function not provided');
      return;
    }

    try {
      const result = await onSendMessage({
        variables: { hiring_post_id: opportunity.id }
      });
      
      if (result.data?.createOrFindStudentConversation?.success) {
        const conversationId = result.data.createOrFindStudentConversation.conversation.id;
        window.location.href = `/student/messages?conversation_id=${conversationId}`;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
      alert('Unable to send message. Please try again.');
    }
  };

  const formatPostedDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getPositionTypeBadge = () => {
    if (opportunity.position_type === 'full-time' || opportunity.position_type === 'full_time') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Full-Time
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
        Internship
      </span>
    );
  };

  const advisorName = opportunity.advisor_display_name || opportunity.user?.name || 'Advisor';

  // Support both legacy avatar filenames and new avatar URLs.
  const profilePicture = opportunity.user?.profile_picture;
  const avatarUrl = profilePicture
    ? (/^https?:\/\//i.test(profilePicture)
      ? profilePicture
      : `https://s3-us-west-2.amazonaws.com/findbob-production/images/${profilePicture}`)
    : null;

  // Get firm name from opportunity or default
  const firmName = opportunity.firm_name || opportunity.firmName || 'Financial Services Firm';

  // Get advisor's professional designations/licenses
  const getAdvisorDesignations = () => {
    const user = opportunity.user;
    const inventoryUser = user?.inventory_user;
    
    // Try to get designations from inventory_user first, then user
    const designations = inventoryUser?.designations || user?.designations || [];
    const licenses = inventoryUser?.licenses || user?.licenses || [];
    
    // Combine and filter
    const allCredentials = [...designations, ...licenses].filter(Boolean);
    
    if (allCredentials.length > 0) {
      return allCredentials.join(', ');
    }
    
    // Fallback to a generic title
    return 'Financial Advisor';
  };

  // Get years of experience
  const yearsExperience = opportunity.user?.years_practicing || opportunity.user?.inventory_user?.years_practicing || '10+';

  // Get advisor headline/bio
  const advisorHeadline = opportunity.user?.headline || opportunity.user?.inventory_user?.headline || 
    'Passionate about mentoring the next generation of financial advisors.';

  // Parse requirements if it's a JSON string, or split by newlines
  let requirements = [];
  if (opportunity.requirements) {
    try {
      requirements = typeof opportunity.requirements === 'string'
        ? (opportunity.requirements.startsWith('[') ? JSON.parse(opportunity.requirements) : opportunity.requirements.split('\n').filter(r => r.trim()))
        : opportunity.requirements;
    } catch (e) {
      requirements = opportunity.requirements.split('\n').filter(r => r.trim());
    }
  }

  // Parse qualifications as learning objectives
  let learningObjectives = [];
  if (opportunity.qualifications) {
    try {
      learningObjectives = typeof opportunity.qualifications === 'string'
        ? (opportunity.qualifications.startsWith('[') ? JSON.parse(opportunity.qualifications) : opportunity.qualifications.split('\n').filter(q => q.trim()))
        : opportunity.qualifications;
    } catch (e) {
      learningObjectives = opportunity.qualifications.split('\n').filter(q => q.trim());
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showNavbar && (
        <Navbar
          currentUser={currentUser}
          currentPath="/student/dashboard"
          brandLogoUrl={brandLogoUrl}
          primaryColor={colors.primary}
          secondaryColor={colors.secondary}
        />
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Back Button */}
        <a
          href={backLinkUrl}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLinkText}
        </a>

        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                {getPositionTypeBadge()}
                <span className="text-sm text-gray-500">
                  Posted {formatPostedDate(opportunity.published_at)}
                </span>
                {isMessagingWithAdvisor && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    style={{
                      backgroundColor: `${colors.primary}0f`,
                      borderColor: `${colors.primary}33`,
                      color: colors.primary,
                    }}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    In conversation
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold mb-3 text-gray-900">{opportunity.title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{opportunity.location}</span>
                  {opportunity.remote && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${colors.primary}1a`, color: colors.primary }}
                    >
                      Remote
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>{firmName}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {showSaveShare && (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                >
                  <Bookmark className={`h-5 w-5 ${isSaved ? 'fill-current' : ''}`} />
                  {isSaved ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                >
                  <Share className="h-5 w-5" />
                  Share
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Post Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Overview</h2>
              <p className="text-gray-600 leading-relaxed">{opportunity.description}</p>
              <p className="text-gray-600 leading-relaxed mt-4">
                Join our team for a hands-on experience where you'll work directly with clients,
                learn industry-leading financial planning software, and gain real-world experience
                in wealth management. This is an excellent opportunity to build your career in
                financial services while working alongside experienced professionals.
              </p>
            </div>

            {/* Requirements */}
            {requirements.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Requirements</h2>
                <ul className="space-y-3">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div
                        className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${colors.primary}1a` }}
                      >
                        <span className="text-sm" style={{ color: colors.primary }}>✓</span>
                      </div>
                      <span className="text-gray-600">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* What You'll Learn / Qualifications */}
            {learningObjectives.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Qualifications</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {learningObjectives.map((obj, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Briefcase
                        className="h-5 w-5 flex-shrink-0 mt-1"
                        style={{ color: colors.primary }}
                      />
                      <div>
                        <p className="text-sm text-gray-600">
                          {typeof obj === 'string' ? obj : obj.title || obj.description || ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Position Details */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Position Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {opportunity.duration && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Duration</p>
                      <p className="font-medium">{opportunity.duration}</p>
                    </div>
                  </div>
                )}
                {opportunity.start_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Start Date</p>
                      <p className="font-medium">{opportunity.start_date}</p>
                    </div>
                  </div>
                )}
                {opportunity.compensation && (
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Compensation</p>
                      <p className="font-medium">{opportunity.compensation}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Work Mode</p>
                    <p className="font-medium">{opportunity.remote ? 'Remote/Hybrid' : 'On-site'}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column - Advisor Info & Actions */}
          <div className="lg:sticky lg:top-24 space-y-6 self-start">
            {/* Advisor Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="text-center mb-4">
                <Avatar
                  src={avatarUrl}
                  name={advisorName}
                  size="3xl"
                  className="mx-auto mb-4 border-2 border-gray-200"
                />

                <h3 className="text-xl font-semibold mb-1">{advisorName}</h3>
                <p className="text-sm text-gray-600 mb-1">{getAdvisorDesignations()}</p>
                <p className="text-sm text-gray-600">{firmName}</p>
              </div>

              <div className="mb-6">
                <button 
                  onClick={handleSendMessage}
                  disabled={isOwnPost}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium ${
                    isOwnPost 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'text-white'
                  }`}
                  style={isOwnPost ? {} : { backgroundColor: colors.primary }}
                  title={isOwnPost ? 'You cannot message yourself' : 'Send a message to this advisor'}
                  onMouseEnter={(e) => {
                    if (!isOwnPost) e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    if (!isOwnPost) e.currentTarget.style.opacity = '1';
                  }}
                >
                  <Mail className="h-5 w-5" />
                  {isMessagingWithAdvisor ? 'Continue Conversation' : 'Send Message'}
                </button>
                {isMessagingWithAdvisor && (
                  <p className="text-center text-xs text-gray-600 mt-2">
                    You're already in conversation with this advisor
                  </p>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-semibold mb-3">About the Advisor</h4>
                <p className="text-sm text-gray-600 mb-4">
                  {advisorHeadline}
                </p>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-gray-400" />
                    <span>{yearsExperience} years experience</span>
                  </div>
                  {(opportunity.linkedin_url || opportunity.linkedinUrl) && (
                    <div className="flex items-center gap-2">
                      <Linkedin className="h-4 w-4 text-gray-400" />
                      <a 
                        href={opportunity.linkedin_url || opportunity.linkedinUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="hover:underline"
                        style={{ color: colors.primary }}
                      >
                        LinkedIn Profile
                      </a>
                    </div>
                  )}
                </div>

                {(opportunity.user?.designations?.length > 0 || opportunity.user?.advisor_licenses?.length > 0) && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {opportunity.user?.designations?.map((designation, index) => (
                      <span key={`designation-${index}`} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {designation}
                      </span>
                    ))}
                  {opportunity.user?.advisor_licenses?.map((license, index) => (
                      <span
                        key={`license-${index}`}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: `${colors.primary}1a`, color: colors.primary }}
                      >
                        {license}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Application Instructions */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold mb-3">How to Apply</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <p>1. Send a message introducing yourself</p>
                <p>2. Attach your resume and cover letter</p>
                <p>3. Mention your availability and why you're interested</p>
                <p>4. Wait for a response (typically within 3-5 business days)</p>
              </div>
            </div>

            {/* Quick Facts */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold mb-3">Quick Facts</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Posted:</span>
                  <span className="font-medium">
                    {formatPostedDate(opportunity.published_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Views:</span>
                  <span className="font-medium">{opportunity.views_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Applications:</span>
                  <span className="font-medium">{opportunity.applications_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    opportunity.status === 'published' 
                      ? 'bg-green-100 text-green-800' 
                      : opportunity.status === 'closed' 
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {opportunity.status === 'published' ? 'Active' : opportunity.status.charAt(0).toUpperCase() + opportunity.status.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

OpportunityDetail.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  opportunity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    position_type: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    remote: PropTypes.bool,
    description: PropTypes.string,
    requirements: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    qualifications: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    compensation: PropTypes.string,
    duration: PropTypes.string,
    start_date: PropTypes.string,
    application_instructions: PropTypes.string,
    published_at: PropTypes.string,
    views_count: PropTypes.number,
    applications_count: PropTypes.number,
    slug: PropTypes.string,
    status: PropTypes.string,
    firm_name: PropTypes.string,
    firmName: PropTypes.string,
    linkedin_url: PropTypes.string,
    linkedinUrl: PropTypes.string,
    advisor_display_name: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      profile_picture: PropTypes.string,
      headline: PropTypes.string,
      years_practicing: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      designations: PropTypes.array,
      licenses: PropTypes.array,
      inventory_user: PropTypes.shape({
        headline: PropTypes.string,
        years_practicing: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        designations: PropTypes.array,
        licenses: PropTypes.array,
      }),
    }),
  }).isRequired,
  onSendMessage: PropTypes.func,
  isOwnPost: PropTypes.bool,
  backLinkUrl: PropTypes.string,
  backLinkText: PropTypes.string,
  showSaveShare: PropTypes.bool,
  showNavbar: PropTypes.bool,
  isMessagingWithAdvisor: PropTypes.bool,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  neutralColor: PropTypes.string,
};

export default OpportunityDetail;

